import { useState } from 'react';
import type { ID } from '@/model/Common.model';
import { _notNil } from '@/littledash';
import type { ExtendedMetadataItem } from './IndividualSaveRepeater.utils';

interface UseTableEditStateReturn<T = ExtendedMetadataItem | boolean> {
  editingItems: Set<ID>;
  tempValues: Record<string, T>;
  isEditing: (id: ID) => boolean;
  startEdit: (id: ID, currentValue: T) => void;
  cancelEdit: (id: ID) => void;
  saveEdit: (id: ID, saveFn: (value: T) => Promise<void>) => Promise<void>;
  updateTempValue: (id: ID, value: T) => void;
}

export const useTableEditState = <T = ExtendedMetadataItem | boolean>(): UseTableEditStateReturn<T> => {
  const [editingItems, setEditingItems] = useState<Set<ID>>(new Set());
  const [tempValues, setTempValues] = useState<Record<string, T>>({});

  const isEditing = (id: ID): boolean => editingItems.has(id);

  const startEdit = (id: ID, currentValue: T) => {
    setEditingItems((prev) => new Set(prev).add(id));
    setTempValues((prev) => ({ ...prev, [String(id)]: currentValue }));
  };

  const cancelEdit = (id: ID) => {
    setEditingItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    setTempValues((prev) => {
      const { [String(id)]: removed, ...rest } = prev;
      return rest;
    });
  };

  const saveEdit = async (id: ID, saveFn: (value: T) => Promise<void>) => {
    const value = tempValues[String(id)];
    if (_notNil(value)) {
      await saveFn(value);
      setEditingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      setTempValues((prev) => {
        const { [String(id)]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const updateTempValue = (id: ID, value: T) => {
    setTempValues((prev) => ({ ...prev, [String(id)]: value }));
  };

  return {
    editingItems,
    tempValues,
    isEditing,
    startEdit,
    cancelEdit,
    saveEdit,
    updateTempValue,
  };
};
